/**
 * The finale — the street at dusk (left) and the factory at night (right).
 * Both are anchored to the seam and to the bottom: the roof the hero stands
 * on is drawn by the stylesheet, over the lower edge.
 *
 * @module scenes/finale
 */

import { MARK_LEFT, MARK_RIGHT } from '../components/icons.js';
import { circle, rect, scene } from './svg.js';

// A bat, wings spread, centred on 0 0 and 100 units wide.
const BAT =
    'M0 -6 L3 -7 L5 -16 L7.5 -6 C16 -8 26 -12 34 -18 C42 -12 48 -6 50 3 Q44 0 40 5 Q35 2 30 9 ' +
    'Q23 5 17 11 Q10 8 6 13 Q3 14 0 19 Q-3 14 -6 13 Q-10 8 -17 11 Q-23 5 -30 9 Q-35 2 -40 5 ' +
    'Q-44 0 -50 3 C-48 -6 -42 -12 -34 -18 C-26 -12 -16 -8 -7.5 -6 L-5 -16 L-3 -7 Z';

/**
 * Easter egg: a searchlight on the roof of the first tower. Lit (finale.js),
 * it throws a bat on the cloud above the skyline while dusk falls.
 *
 * @returns {{ lamp: string, signal: string }}
 */
function batSignal() {
    // The lens faces the cloud: 40° up from the roof, at 96 682.
    const lamp =
        rect(68, 700, 24, 10, 'fill-ink') +
        '<g transform="rotate(-40 85 692)">' +
        rect(70, 684, 30, 16, 'fill-ink', 'rx="3"') +
        rect(98, 685, 5, 14, '', 'class="batsignal__lens" rx="1.5"') +
        '</g>';

    const signal =
        '<defs><linearGradient id="batsignal-beam" gradientUnits="userSpaceOnUse" x1="96" y1="682" x2="215" y2="440">' +
        '<stop offset="0" class="batsignal__glow" stop-opacity=".75"/>' +
        '<stop offset="1" class="batsignal__glow" stop-opacity=".12"/>' +
        '</linearGradient></defs>' +
        '<polygon class="batsignal__beam" points="89.7 679 143 405 287 475 102.3 685" fill="url(#batsignal-beam)"/>' +
        '<ellipse class="batsignal__halo fill-amber" cx="215" cy="440" rx="104" ry="56" fill-opacity=".28"/>' +
        '<ellipse class="batsignal__disc fill-amber" cx="215" cy="440" rx="86" ry="44"/>' +
        `<path class="batsignal__bat fill-ink" transform="translate(215 442) scale(1.32)" d="${BAT}"/>`;

    return { lamp, signal: `<g class="batsignal__signal">${signal}</g>` };
}

/** @returns {import('../lib/html.js').Markup} */
export function finaleFront() {
    // A distant skyline, low enough that the hero in front is the tallest
    // thing on screen; the sun sets just above it.
    const towers = [
        [0, 120, 190],
        [130, 90, 140],
        [235, 130, 230],
        [380, 80, 160],
        [470, 120, 260],
        [605, 95, 180],
        [715, 85, 220],
    ];

    const { lamp, signal } = batSignal();

    // Dusk deepens over the sky while the signal is lit.
    let out =
        rect(0, 0, 800, 900, 'fill-paper') +
        rect(0, 0, 800, 900, 'batsignal__dusk fill-ink') +
        circle(560, 660, 105, 'fill-red') +
        rect(90, 450, 170, 20, 'fill-paper-2', 'rx="10"') +
        rect(140, 432, 80, 26, 'fill-paper-2', 'rx="13"') +
        rect(560, 380, 120, 16, 'fill-paper-2', 'rx="8"');

    towers.forEach(([x, width, height], index) => {
        const top = 900 - height;
        let tower = rect(x, top, width, height, 'fill-paper-3');

        for (let y = top + 18; y < 880; y += 30) {
            for (let windowX = x + 12; windowX < x + width - 16; windowX += 22) {
                const lit = (windowX + y) % 7 === 0;
                tower += rect(windowX, y, 10, 14, lit ? 'fill-red' : 'fill-paper-2', lit ? 'opacity=".55"' : '');
            }
        }

        // The first tower carries the searchlight, and answers the pointer.
        out += index === 0 ? `<g class="batsignal__tower">${tower}${lamp}</g>${signal}` : tower;
    });

    return scene('0 0 800 900', out, 'xMaxYMax slice');
}

/** @returns {import('../lib/html.js').Markup} */
export function finaleBack() {
    const stars = [
        [60, 300],
        [180, 360],
        [330, 260],
        [450, 330],
        [590, 240],
        [700, 380],
        [760, 290],
        [400, 450],
        [120, 470],
    ];

    let out = rect(0, 0, 800, 900, 'fill-night');

    stars.forEach(([cx, cy], index) => {
        out += circle(cx, cy, index % 3 ? 1.4 : 2, 'fill-dim', 'opacity=".6"');
    });

    // The moon is the mark of the logo, in outline.
    out +=
        '<g transform="translate(560 330) scale(.42) translate(-259 -273)" class="stroke-red" stroke-width="5">' +
        `<path d="${MARK_LEFT}"/><path d="${MARK_RIGHT}"/></g>`;

    // Far, low sawtooth roofs, two chimneys with a light on top, smoke.
    out +=
        '<path d="M0 760 L80 705 L80 760 L180 705 L180 760 L280 705 L280 760 L380 705 L380 760 L480 705 ' +
        'L480 760 L580 705 L580 760 L680 705 L680 760 L800 720 L800 900 L0 900 Z" class="fill-night-2"/>' +
        rect(440, 600, 34, 140, 'fill-night-2') +
        rect(440, 594, 34, 8, 'fill-amber') +
        rect(610, 640, 28, 100, 'fill-night-2') +
        rect(610, 634, 28, 6, 'fill-red') +
        '<g class="fill-night-3">' +
        circle(458, 560, 18) +
        circle(478, 530, 24) +
        circle(506, 496, 30) +
        '</g>';

    // Lit windows along the roofs.
    for (let y = 772; y < 880; y += 34) {
        for (let x = 20; x < 790; x += 40) {
            const lit = (x * 7 + y) % 3 === 0;
            out += rect(x, y, 20, 16, lit ? 'fill-amber' : 'fill-night-3', lit ? 'opacity=".7"' : '');
        }
    }

    return scene('0 0 800 900', out, 'xMinYMax slice');
}
